import React from 'react';
import { ActiveFilter, CrawlfishJob, User } from '../../crawler.interface';
import './jobList.scss';
import Table from '../../../components/table/table';
import JobRow from './jobRow/jobRow';
import { TableHeaderDataProps } from '../../../components/table/table.interface';
import FilterSection from '../../../components/filterSection/filterSection';
import UserContext, { UserContextInterface } from '../../../contexts/user-context';

export interface JobListProps {
    totalJobs: number;
    filteredJobs: CrawlfishJob[];
    sortIndex: number;
    activeColumn: string;
    activeFilters: ActiveFilter[];
    emptyResultsMessage?: JSX.Element | null;
    sortByColumn: (columnHeader: string) => void;
    recreateExistingJob: (newJob: CrawlfishJob) => void;
    deleteJob: (jobId: number) => void;
    archiveJob: (jobId: number) => void;
    handleFilterSubmit?: (filter: ActiveFilter) => void;
    handleFilterRemove?: (filter: ActiveFilter) => void;
    clearActiveFilters?: () => void;
    setJobListPage?: (pageOffset: number) => void;
    activePage?: number;
    isFilterInProgress: boolean;
}

function JobList({ totalJobs, filteredJobs, sortIndex, activeColumn, activeFilters, emptyResultsMessage,
    sortByColumn, recreateExistingJob, deleteJob, archiveJob,
    handleFilterSubmit, handleFilterRemove, clearActiveFilters, setJobListPage, activePage, isFilterInProgress }: JobListProps) {

    const { crawlfishUsers } = React.useContext<UserContextInterface>(UserContext);
    const [crawlfishUserNames, setCrawlfishUserNames] = React.useState<string[]>([]);

    React.useEffect(() => {
        init();
    }, []);

    async function init(): Promise<void> {
        if (crawlfishUsers && crawlfishUsers?.length > 0) {
            const userNames = crawlfishUsers.map((user: User) => user.username);
            userNames.sort((a, b) => a.localeCompare(b));
            setCrawlfishUserNames(userNames);
        }
    }

    const headerData: TableHeaderDataProps[] = [
        {
            id: 1,
            title: 'Job Name',
            name: 'name',
            className: 'job-name',
            width: 18,
            func: sortByColumn
        },
        {
            id: 2,
            title: 'Job Owner',
            name: 'owner',
            className: 'job-owner',
            width: 18,
            func: sortByColumn
        },
        {
            id: 3,
            title: 'Domain(s)',
            name: 'domains',
            className: 'domains',
            width: 18,
            func: sortByColumn,
            sortDisabled: true
        },
        {
            id: 4,
            title: 'Status',
            name: 'status',
            className: 'status',
            width: 12,
            func: sortByColumn
        },
        {
            id: 5,
            title: 'Created',
            name: 'created_at',
            className: 'created-at',
            width: 14,
            func: sortByColumn
        },
        {
            id: 6,
            title: 'Reviewed',
            name: 'reviewed',
            className: 'reviewed',
            width: 10,
            func: sortByColumn,
        },
        {
            id: 7,
            title: '',
            name: '',
            className: 'no-header',
            width: 10,
            func: () => null,
        },

    ];

    const rows: JSX.Element[] = [];
    if (filteredJobs && filteredJobs.length > 0) {
        filteredJobs.forEach((job: CrawlfishJob) => {
            const newRow = <JobRow key={job?.id} jobData={job} crawlfishUsers={crawlfishUserNames} recreateExistingJob={recreateExistingJob} deleteJob={deleteJob} archiveJob={archiveJob} />;
            rows.push(newRow);
        });
    }

    return (
        <>
            <FilterSection activeFilters={activeFilters}
                filterOptions={['Name', 'Owner', 'URI', 'Created Date Range', 'Reviewed', 'Status']}
                pageType="jobs"
                crawlfishUsers={crawlfishUserNames}
                handleFilterSubmit={handleFilterSubmit}
                handleFilterRemove={handleFilterRemove}
                clearActiveFilters={clearActiveFilters}
            />
            <div className="job-table">
                <Table bodyData={rows}
                    hasCheckAllOption={false}
                    headerData={headerData}
                    sortIndex={sortIndex}
                    pageDataLimit={200}
                    totalResults={totalJobs}
                    activeColumn={activeColumn}
                    message={emptyResultsMessage}
                    setPageServerSide={setJobListPage}
                    isFiltering={isFilterInProgress}
                    activePage={activePage}
                ></Table>
            </div>
        </>

    );
}

export default JobList;